import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { BqInput, BqIcon, BqButton } from '@beeq/react'
import { Formik, FieldArray } from 'formik'
import {
  StyledBqDialog,
  StyledHelperTextWrapper,
  StyledBqCheckbox,
  StyledFieldset,
  StyledPermissionsWrapper,
  StyledFooter,
  StyledContentWrapper,
  StyledCheckboxList,
  StyledPermissionHelperText
} from './styles'
import { Text } from '../../../constants/TextConstants'
import { useBoolean } from '../../../hooks/useBoolean'

/**
 * @typedef {Object} Permission
 * @property {string} _id
 * @property {string} code
 * @property {string} label
 */

/**
 * @typedef {Object} Role
 * @property {string} _id
 * @property {string} name
 * @property {string[]} permissions
 */

/**
 * @typedef {Object} CreateRoleProps
 * @property {boolean} open
 * @property {Role?} role
 * @property {Permission[]} permissions
 * @property {() => void} onClose
 * @property {(values: {name: string, permissions: string[]} ) => void} onSubmit
 */

/**
 * @param {CreateRoleProps} props
 * @returns {import('react').ReactElement}
 */
export default function CreateRole({ open, role, permissions, roles, onClose, onSubmit }) {
  // NOTE: enable enableReinitialize on formik doubles the entry of permissions.
  // This local state is used to take out of the dom the form thus upon re-render will start with a new initialValues
  const [isOpen, { on, off }] = useBoolean()

  const initialValues = useMemo(() => (role ?? { name: '', permissions: [] }), [role])

  useEffect(() => {
    if (open) on()
  }, [open])

  if (!isOpen) return null

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={(values) => {
        const errors = {}

        if (values.name && roles.some((role) => role._id !== initialValues._id && role.name.toLowerCase() === values.name.toLowerCase())) {
          errors.name = Text.RoleDuplicateName
        } else if (!values.name) {
          errors.name = ''
        }

        if (values.permissions.length) {
          const role = roles.find((role) =>
            role._id !== initialValues._id &&
            role.permissions.length === values.permissions.length &&
            role.permissions.every((permission) => values.permissions.includes(permission))
          )

          if (role) {
            errors.permissions = Text.ActivePermissionsDuplicatePermission.replace('{{name}}', role.name)
          }
        } else {
          errors.permissions = ''
        }

        return errors
      }}
    >
      {({ submitForm, values, errors, handleChange, handleBlur, isValid, isSubmitting, dirty }) => (
        <StyledBqDialog
          open={open}
          onBqClose={onClose}
          onBqCancel={onClose}
          onBqAfterClose={off}
        >
          <span className="h5 bold" slot="title">{role ? Text.EditRolePermissions : Text.CreateRole}</span>

          <StyledContentWrapper>
            {!role && <div>{Text.CreateRoleDescription}</div>}
            <BqInput
              name='name'
              value={values.name}
              onBqInput={handleChange}
              onBqBlur={handleBlur}
              validation-status={errors.name ? 'error' : undefined}
            >
              <label slot="label">{Text.RoleLabel}</label>
              <StyledHelperTextWrapper slot="helper-text" >
                {!errors.name ? <><BqIcon name="info" weight="bold" />{Text.RoleHelper}</> : null}
                {errors.name ? <><BqIcon name="x-circle" weight="bold" />{errors.name} </> : null}
              </StyledHelperTextWrapper>
            </BqInput>
            <StyledCheckboxList>
              <label className='caption'>{Text.ActivePermissionsLabel}</label>
              <StyledFieldset>
                <StyledPermissionsWrapper>
                  <FieldArray name="permissions">
                    {({ push, remove }) => (
                      permissions.map((permission, index) => (
                        <StyledBqCheckbox
                          key={permission._id}
                          name={`permissions.${index}`}
                          checked={values.permissions.includes(permission._id)}
                          disabled={!!errors.name}
                          onBqChange={(event) => {
                            if (event.detail.checked) {
                              push(permission._id)
                            } else {
                              remove(values.permissions.indexOf(permission._id))
                            }
                          }}
                          onBqBlur={handleBlur}
                        >
                          {permission.label}
                        </StyledBqCheckbox>
                      ))
                    )}
                  </FieldArray>
                </StyledPermissionsWrapper>
              </StyledFieldset>
              {!errors.permissions ? <span className="caption">{Text.ActivePermissionsHelperText}</span> : null}
              {errors.permissions ? <StyledPermissionHelperText className="caption"><BqIcon name="x-circle" weight="bold" />{errors.permissions} </StyledPermissionHelperText> : null}
            </StyledCheckboxList>
            <StyledFooter slot="footer">
              <BqButton appearance="link" onBqClick={onClose}>{Text.Cancel}</BqButton>
              <BqButton
                onBqClick={submitForm}
                disabled={!isValid || !dirty || isSubmitting}
                loading={isSubmitting}>
                {initialValues._id ? Text.SaveChanges : Text.Create}
              </BqButton>
            </StyledFooter>
          </StyledContentWrapper>
        </StyledBqDialog >
      )}
    </Formik>
  )
}

CreateRole.propTypes = {
  open: PropTypes.bool.isRequired,
  role: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    __v: PropTypes.number.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired
  }),
  permissions: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  }).isRequired).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}
